import * as d3 from "d3";
import * as d3Zoom from "d3-zoom";
import { Size } from "@hokuto/jam-core";
import { ANIMATION_DURATION, MAX_ZOOM, MIN_ZOOM } from "../../const.js";
import {
  ZoomControlsObservable,
  ZoomControlsProps,
  ZoomType,
} from "../../types.js";
import { Observable } from "../observable.js";
import { ZoomNavigation } from "./navigation.js";

export type ZoomNavigationType = Omit<typeof ZoomNavigation, "prototype">;

export type ZoomNavigationAction<K extends keyof ZoomNavigationType> =
  (typeof ZoomNavigation)[K];

export type ZoomNavigationActionParams<K extends keyof ZoomNavigationType> =
  Parameters<ZoomNavigationAction<K>>["0"];

export type ZoomNavigationParams<
  K extends keyof ZoomNavigationType,
  P extends keyof ZoomNavigationActionParams<K>
> = Pick<ZoomNavigationActionParams<K>, P>;

export class ZoomControls<T extends Element> {
  element: T;
  zoom: ZoomType<T>;
  initialSize: Size;
  observable: ZoomControlsObservable;
  private readonly duration?: number;
  constructor(args: ZoomControlsProps<T>) {
    const {
      element,
      zoom,
      initialSize,
      observable,
      duration = ANIMATION_DURATION,
    } = args;
    this.element = element;
    this.zoom = zoom;
    this.initialSize = initialSize;
    this.observable = observable;
    this.duration = duration;
  }

  transformation() {
    const { current, previous } = this.observable.get();
    return current || previous;
  }
  zoomIn(args: ZoomNavigationParams<"zoomIn", "value" | "duration"> = {}) {
    return ZoomNavigation.zoomIn({
      duration: this.duration,
      ...args,
      element: this.element,
      zoom: this.zoom,
    });
  }
  zoomOut(args: ZoomNavigationParams<"zoomOut", "value" | "duration"> = {}) {
    return ZoomNavigation.zoomOut({
      duration: this.duration,
      ...args,
      element: this.element,
      zoom: this.zoom,
    });
  }

  panLeft(args: ZoomNavigationParams<"panLeft", "value" | "duration"> = {}) {
    return ZoomNavigation.panLeft({
      duration: this.duration,
      ...args,
      element: this.element,
      zoom: this.zoom,
    });
  }
  panRight(args: ZoomNavigationParams<"panRight", "value" | "duration"> = {}) {
    return ZoomNavigation.panRight({
      duration: this.duration,
      ...args,
      element: this.element,
      zoom: this.zoom,
    });
  }
  panTop(args: ZoomNavigationParams<"panTop", "value" | "duration"> = {}) {
    return ZoomNavigation.panTop({
      duration: this.duration,
      ...args,
      element: this.element,
      zoom: this.zoom,
    });
  }
  panBottom(
    args: ZoomNavigationParams<"panBottom", "value" | "duration"> = {}
  ) {
    return ZoomNavigation.panBottom({
      duration: this.duration,
      ...args,
      element: this.element,
      zoom: this.zoom,
    });
  }
  transform(args: ZoomNavigationParams<"transform", "value" | "duration">) {
    return ZoomNavigation.transform({
      duration: this.duration,
      ...args,
      element: this.element,
      zoom: this.zoom,
    });
  }

  center(
    args: ZoomNavigationParams<"center", "width" | "height" | "duration">
  ) {
    return ZoomNavigation.center({
      duration: this.duration,
      ...args,
      element: this.element,
      zoom: this.zoom,
    });
  }

  reset(duration = this.duration) {
    const { width: currentWidth, height: currentHeight } =
      this.element.getBoundingClientRect();
    const { width: initialWidth, height: initialHeight } = this.initialSize;
    const value = new d3Zoom.ZoomTransform(
      1,
      (currentWidth - initialWidth) / 2,
      (currentHeight - initialHeight) / 2
    );

    this.transform({ duration, value });
  }

  static create<T extends Element>(args: {
    element: T;
    initialSize: Size;
    duration?: number;
    minZoom?: number;
    maxZoom?: number;
  }) {
    const {
      element,
      initialSize,
      duration = ANIMATION_DURATION,
      maxZoom = MAX_ZOOM,
      minZoom = MIN_ZOOM,
    } = args;

    const observable: ZoomControlsObservable = new Observable({});

    const zoom: ZoomType<T> = d3Zoom
      .zoom<T, unknown>()
      .duration(duration)
      .scaleExtent([minZoom, maxZoom])
      .on(
        "zoom",
        (e: { transform: d3Zoom.ZoomTransform; sourceEvent?: MouseEvent }) => {
          const { transform: current } = e;
          const previous = observable.getValue("current");
          if (
            previous &&
            current.k === previous.k &&
            current.x === previous.x &&
            current.y === previous.y
          )
            return;
          observable.set({
            current,
            previous,
            action: "zoom",
            sourceEvent: e.sourceEvent ?? undefined,
          });
        }
      )
      .on(
        "start",
        (e: { transform: d3Zoom.ZoomTransform; sourceEvent?: MouseEvent }) => {
          observable.set({
            current: e.transform,
            previous: undefined,
            action: "start",
            sourceEvent: e.sourceEvent ?? undefined,
          });
        }
      )
      .on(
        "end",
        (e: { transform: d3Zoom.ZoomTransform; sourceEvent?: MouseEvent }) => {
          observable.set({
            current: undefined,
            previous: e.transform,
            action: "end",
            sourceEvent: e.sourceEvent ?? undefined,
          });
        }
      );

    zoom.wheelDelta(function wheelDelta(event) {
      return (
        -event.deltaY *
        (event.deltaMode === 1 ? 0.05 : event.deltaMode ? 1 : 0.002) *
        (event.ctrlKey ? 5 : 0.5) *
        (event.deltaY >= 0 ? 0.5 : 1)
      );
    });

    d3.select(element).call(zoom);
    return new ZoomControls({
      element,
      zoom,
      initialSize,
      observable,
      duration,
    });
  }
}
